import { Suspense, lazy, useState } from 'react'
import { Link, router } from '@inertiajs/react'

import Layout from '../../components/Layout'
import { GravityStars } from '../../components/GravityStars'
import { ModelViewer } from '../../components/ModelViewer'
import { MakerDetails } from '../../components/MakerDetails'
import { ProductDrop, productDropFor } from '../../components/ProductDrop'
import { StoreImage } from '../../components/StoreImage'
import SelectedVideo from '../../components/SelectedVideo'
import VisibleMedia from '../../components/VisibleMedia'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BrushIcon,
  CheckIcon,
  CloseIcon,
  EditIcon,
  GlossIcon,
  ImagePlaceholderIcon,
  TrashIcon,
  UniqueIcon,
} from '../../components/Icons'
import { StampDialog } from '../../components/StampDialog'
import { DEFAULT_STAMP_STYLE, fontCss } from '../../components/StampStep'
import { useTranslation } from '@/lib/i18n'
import { useCurrency, type Prices } from '@/lib/currency'
import { trackEvent } from '@/lib/track'
import type { StampStyle } from '@/lib/useCustomizeDraft'
import { Button } from '@/components/ui/button'
import { splitName } from '@/lib/productName'
import CardMeta from '@/components/CardMeta'
import VapePicker, { type VapeOption } from '@/components/VapePicker'
import { useIsCompact } from '@/lib/useMediaQuery'

interface ProductImage {
  id: number
  url: string
}

interface Product {
  id: number
  name: string
  searchLabel?: string | null
  compatibility?: string | null
  contents?: string | null
  assetSlug: string
  description: string
  price: number
  /** Final tags by currency; USD always, RUB / BYN when set. */
  prices: Prices
  stock: number
  inStock: boolean
  tag: string | null
  material: string | null
  images: ProductImage[]
  /** Web path to an optimized GLB; null means this piece has photos only. */
  modelUrl: string | null
  /** Whether the workshop can cut a line of text into this piece. */
  customizable: boolean
  oneOfOne: boolean
  /** Transparent render under public/cutouts, or null — feeds the
   *  made-by-hand marquee. */
  cutoutUrl: string | null
  /** Reveal clip under public/product-videos, or null — the hero
   *  slider's last slide. */
  videoUrl: string | null
  /** Made to order: no stock line, a lead time and the custom flow. */
  custom: boolean
  /** The line the piece belongs to; picks the "what's in the box" list. */
  category: string | null
  /** The piece in use plus a do / don't pair, from public/fit-guide. */
  fitGuide: { videoUrl: string; okUrl: string; noUrl: string } | null
  /** Portrait insertion demo in the handmade section's Case tab. */
  demoVideoUrl: string | null
  /** On a custom slot: the stocked pieces of the same line. */
  examples: Example[]
  /** On a vape case: the devices it can be cut for. The buy row asks
   *  which before the piece can go in the bag; empty elsewhere. */
  vapes: VapeOption[]
  /** What `vapes` lists: the device on a case, the magnetic tip on the
   *  stand. Null when the piece asks nothing. */
  choiceKind?: 'vape' | 'pin' | 'airpods' | null
}

interface Example {
  id: number
  name: string
  imageUrl: string
  cutoutUrl: string | null
  /** A piece that left the shop is still past work, just not a link. */
  listed: boolean
}

interface IncludedLine {
  ok: boolean
  text: string
}

interface OtherProduct {
  id: number
  name: string
  assetSlug: string
  price: number
  prices: Prices
  inStock: boolean
  material: string | null
  /** The line the piece belongs to — the card's own sub-line. */
  category: string | null
  /** Always present: the controller only sends pieces that have one. */
  cutoutUrl: string
}

interface Props {
  product: Product
  /** The rest of the shop for "choose your piece". The `/test-card`
   *  bench renders this page without it, hence the default. */
  others?: OtherProduct[]
}

/**
 * The ripple button carries `motion`, and every page of the shop is in
 * one eager bundle (see entrypoints/inertia.tsx) — so it is pulled in
 * on demand, the way the 3D viewer is. Until it lands the plain button
 * holds the slot, identical but for the ripple.
 */
const RippleButton = lazy(() =>
  import('@/components/ui/ripple-button').then((m) => ({
    default: m.RippleButton,
  }))
)

const ICON_BUTTON_CLASS = 'size-12 shrink-0 rounded-xl p-0 max-[559px]:size-13'

/**
 * V3 · Product page — from "SSS_bug_lighter_page".
 *
 * A split hero: the media slider fills the left half, the buy panel the
 * right — display-size lowercase title over the bubble wash, the piece
 * and its badges as cards, then the options and the glinting accent
 * CTA. Below it the made-by-hand band (case / materials / care tabs over the
 * spec table) and the rest of the shop as "choose your piece".
 *
 * The money actions stay the page's default on every piece; customising
 * remains the option beside them (the committed line can be edited or
 * taken back off), exactly as before the restyle.
 */
export default function ProductShow({ product, others = [] }: Props) {
  const { t, tx, locale } = useTranslation()
  const { format: formatPrice } = useCurrency()

  const [open, setOpen] = useState(false)
  const [stamp, setStamp] = useState('')
  const [style, setStyle] = useState<StampStyle>(DEFAULT_STAMP_STYLE)
  const isCompact = useIsCompact()
  const hasDemo = Boolean(product.demoVideoUrl || productDropFor(null, product.cutoutUrl))
  const [tab, setTab] = useState<'case' | 'materials'>(
    hasDemo ? 'case' : 'materials'
  )
  // A piece without a demo still starts with its Materials tab.
  const activeTab = tab === 'case' && !hasDemo ? 'materials' : tab
  // The device a vape case is made for. Page state until "add to bag",
  // which posts it with the line; nothing goes in the bag without it.
  const [vapeId, setVapeId] = useState<string | null>(null)
  const [vapeOpen, setVapeOpen] = useState(false)
  const needsVape = product.vapes.length > 0
  const vapeMissing = needsVape && !vapeId
  const choiceKind = product.choiceKind ?? 'vape'

  const { title, sub } = splitName(product.name)
  // Stand identity follows its mandatory pin choice, even with a short artwork title.
  const isMaxStand = product.choiceKind === 'pin'
  // Both locales spell the lighter body "Cricket"; identify its included items
  // from the material rather than introducing a second catalog field.
  const isLighter = (product.material ?? '').toLowerCase().includes('cricket')
  const storyKey = isMaxStand
    ? 'product.max_stand_story'
    : product.category === 'lighter_cases' || isLighter
      ? 'product.lighter_story'
      : product.category === 'vape_cases'
        ? 'product.vape_story'
        : null
  // Only these four artworks have owner-approved Russian stories. Keep all
  // other locales, the custom slot and Piercing on their existing family copy.
  const hasArtworkStory = locale === 'ru' && product.category === 'vape_cases' && !product.custom && [
    'leopard-spikes-vape-case', 'dope-swag-vape-case', 'graffiti-wall-vape-case', 'white-cherub-vape-case',
  ].includes(product.assetSlug)

  // What the box holds is a property of the line, not the piece: a
  // lighter ships its Cricket, a case ships without the device. The
  // stand is the one Apple piece that is not a case.
  const kind = isLighter
    ? 'lighter'
    : product.category === 'vape_cases'
      ? 'vape'
      : isMaxStand
        ? 'stand'
        : product.category === 'apple' || product.category === 'airpods_pro_cases'
          ? 'airpods'
          : null
  const hasTextDetails = kind === 'stand' || kind === 'vape'
  const showDetailsCopy = hasTextDetails && (kind === 'stand' || activeTab === 'materials')
  const included =
    (kind &&
      tx<IncludedLine[] | undefined>(
        `product.included.${kind}${product.custom ? '_custom' : ''}`
      )) ??
    []
  const howSteps = product.custom
    ? (tx<{ title: string; copy: string }[]>('product.how.steps') ?? [])
    : []

  const addToCart = () => {
    trackEvent('add_to_cart', {
      label: product.name,
      meta: {
        product_id: product.id,
        price: product.price,
        quantity: 1,
        vape: vapeId,
        source: 'pdp',
      },
      stage: 'added_to_cart',
    })
    router.post(
      `/cart/add?product_id=${product.id}&quantity=1${vapeId ? `&vape=${vapeId}` : ''}`,
      {},
      { preserveScroll: true, preserveState: true }
    )
  }

  const customize = () => {
    trackEvent('cta_click', {
      label: `Customize · ${product.name}`,
      meta: { product_id: product.id, price: product.price, source: 'pdp' },
      stage: 'clicked_cta',
    })
    setOpen(true)
  }

  const specs = [
    {
      label: t('product.sku'),
      value: `SSS-${String(product.id).padStart(4, '0')}`,
    },
    product.tag ? { label: t('product.series'), value: product.tag } : null,
    product.material
      ? { label: t('product.material'), value: kind === 'stand' ? t('product.made.stand_materials') : product.material }
      : null,
    // A slot has no shelf: it quotes the lead time where a piece quotes
    // how many are left.
    product.custom
      ? { label: t('product.lead_time'), value: t('product.lead_value') }
      : {
          label: t('product.stock'),
          value: t('product.units', { count: product.stock }),
        },
    { label: t('product.origin'), value: t('product.origin_value') },
  ].filter(Boolean) as { label: string; value: string }[]

  // Read the making process first, then the one-of-a-kind result.
  const badges = [
    { label: t('product.badge_hand_painted'), icon: <BrushIcon size={24} /> },
    { label: t(hasTextDetails ? 'product.badge_stand_epoxy' : 'product.badge_sealed_glossy'), icon: <GlossIcon size={24} /> },
    ...(hasTextDetails
      ? [{ label: t(kind === 'stand' ? 'product.badge_stand_unique' : 'product.badge_skin_unique'), icon: <UniqueIcon size={24} /> }]
      : []),
  ]

  // The series-and-stock pill is off the hero. Both facts already have
  // a home further down: the series in the spec table, the stock in the
  // buy button, which says "sold out" when there is none.

  // The optional half of the buy column, and only for a piece the
  // workshop can actually cut text into. Nothing here until the shopper
  // opens the popup; after that the committed line replaces the button
  // and carries its own way back in and out.
  const customization = !product.customizable ? null : stamp ? (
    <div className="flex gap-2.5">
      {/* Shown in the hand it will be cut in. The ink is a swatch, not
          the type colour: a white stamp on the light theme's surface
          would be invisible, and the popup already shows the colour
          where it belongs — on the piece. */}
      <div className="flex h-12 min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-hairline bg-surface-sunk px-3.5 max-[559px]:h-13">
        <span
          aria-hidden="true"
          className="size-4 shrink-0 rounded-full border border-hairline"
          style={{ background: style.color }}
        />
        <span
          className="truncate text-[18px] leading-none"
          style={{ fontFamily: fontCss(style.font) }}
        >
          {stamp}
        </span>
      </div>
      <IconButton label={t('product.edit_customization')} onClick={() => setOpen(true)}>
        <EditIcon size={20} />
      </IconButton>
      <IconButton label={t('product.remove_customization')} onClick={() => setStamp('')}>
        <TrashIcon size={20} />
      </IconButton>
    </div>
  ) : (
    <Button variant="outline" onClick={customize} className="h-12 w-full max-[559px]:h-13">
      {t('product.customize')}
    </Button>
  )

  return (
    <Layout overlayHeader>
      {/* ===== Hero — split media / buy panel, full bleed ===== */}
      <section className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,460px),1fr))]">
        <HeroMedia key={product.id} product={product} />

        {/* Buy panel over the gravity-star field. Children sit on
            `relative` so the canvas stays a floor, not a veil. Top
            padding clears the floating chip header; stacked (below
            ~960) the media is above it, so the clearance drops. */}
        <div className="relative flex flex-col gap-3.5 px-[clamp(20px,4vw,56px)] pb-[clamp(24px,4vw,48px)] pt-[clamp(88px,10vh,120px)] max-[959px]:pt-7">
          {/* The stars clip inside their own wrapper, not the panel:
              the panel has to let the vape list drop below its edge. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
            <GravityStars />
          </div>
          {/* The title takes the free height so the cards sit low in
              the panel, level with the foot of the media card. */}
          <div className="sss-rise relative mb-1.5 flex flex-1 flex-col justify-center text-center [animation-delay:150ms] max-[559px]:order-1">
            {/* Sized off the panel, not the page: the title only has
                half the width, so it must wrap big to fill it. */}
            <h1 className="sss-graffiti text-[clamp(52px,9vw,150px)] font-normal leading-[0.92]">
              {title}
            </h1>

            {sub && (
              <div className="mt-6 flex flex-col items-center gap-3.5">
                <p className="m-0 text-[clamp(14px,1.3vw,20px)] font-semibold leading-[1.4]">
                  {sub}
                </p>
              </div>
            )}
            {product.searchLabel && product.searchLabel !== sub && (
              <p className="mt-5 text-base font-semibold leading-relaxed text-ink">
                {product.searchLabel}
              </p>
            )}
            {product.compatibility && (
              <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-ink-muted">
                {product.compatibility}
              </p>
            )}
          </div>

          {/* The piece — the copy band across the panel. On a phone the
              buy stack overtakes it: the thumb reaches the price and the
              bag before it reads the description. */}
          <div className="sss-rise relative rounded-[18px] border border-hairline bg-surface px-6 py-5.5 transition-[border-color,transform] duration-200 ease-standard [animation-delay:300ms] hover:-translate-y-0.5 hover:border-[color:var(--sss-ink-faint)] max-[559px]:order-4">
            <h2 className="mb-3.5 text-[18px] font-bold tracking-[-0.01em]">
              {t(storyKey ? `${storyKey}.title` : 'product.the_piece')}
            </h2>
            <p data-testid="product-story" className="m-0 whitespace-pre-line text-[15px] leading-[1.6] text-ink-muted">
              {hasArtworkStory || !storyKey ? product.description : t(`${storyKey}.body`)}
            </p>
            {product.contents && (
              <p className="mt-4 text-sm leading-relaxed text-ink-muted">
                {product.contents}
              </p>
            )}
            <MakerDetails isLighter={isLighter} isShell={isLighter || kind === 'vape' || kind === 'airpods'} />
          </div>

          {/* What ships and how it is made, side by side — the two
              questions a shopper asks before the price. Below 560 each
              card is its own row, after the copy. */}
          {!hasTextDetails && product.category !== 'airpods_pro_cases' && (
          <div className="sss-rise grid grid-cols-[repeat(auto-fit,minmax(min(100%,270px),1fr))] gap-3 [animation-delay:380ms] max-[559px]:order-5">
            {included.length > 0 && (
              <InfoCard title={t('product.included.title')}>
                <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                  {included.map((line) => (
                    <li
                      key={line.text}
                      className={`flex items-start gap-3 text-[15px] leading-[1.5] ${
                        line.ok ? 'text-ink' : 'text-ink-muted'
                      }`}
                    >
                      <span
                        className={`flex size-6 shrink-0 items-center justify-center rounded-full border border-hairline ${
                          line.ok ? 'text-signal' : 'text-ink-faint'
                        }`}
                      >
                        {line.ok ? (
                          <CheckIcon size={14} />
                        ) : (
                          <CloseIcon size={14} strokeWidth={2} />
                        )}
                      </span>
                      <span>{line.text}</span>
                    </li>
                  ))}
                </ul>
              </InfoCard>
            )}
            <InfoCard title={t('product.handmade.title')}>
              <p className="m-0 text-[15px] leading-[1.6] text-ink-muted">
                {t('product.handmade.copy')}
              </p>
            </InfoCard>
          </div>
          )}

          {/* A made-to-order slot spells out the deal — paid in full,
              design agreed after, then built — in the accent ring, ahead
              of the money. On a phone it goes straight after the buy
              stack, before the copy. */}
          {product.custom && howSteps.length > 0 && (
            <div className="sss-rise relative rounded-[18px] border border-[color:var(--sss-accent)] bg-surface px-6 py-5.5 [animation-delay:420ms] max-[559px]:order-3">
              <h2 className="mb-4 text-[18px] font-bold tracking-[-0.01em]">
                {t('product.how.title')}
              </h2>
              <ol className="m-0 flex list-none flex-col gap-3.5 p-0">
                {howSteps.map((step, i) => (
                  <li
                    key={step.title}
                    className="grid grid-cols-[32px_minmax(0,1fr)] items-start gap-3.5"
                  >
                    <span className="flex size-8 items-center justify-center rounded-full bg-signal text-[13px] font-bold tabular-nums text-on-accent">
                      {i + 1}
                    </span>
                    <div>
                      <p className="m-0 mb-0.5 text-[15px] font-semibold leading-[1.4]">
                        {step.title}
                      </p>
                      <p className="m-0 text-[14px] leading-[1.5] text-ink-muted">{step.copy}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* The badge card and the buy stack share one row: badges
              left, options + the money actions right, the way the sheet
              pairs them.

              On a phone the row dissolves — `contents` hands both cards
              straight to the panel's own column, so each can take its
              own place in the order: buy stack, then the copy, then the
              badges. */}
          <div
            className={
              'grid gap-3 max-[559px]:contents ' +
              // The device picker needs the width to spell a model out,
              // so on a vape case the pair only sits side by side once
              // each half clears 320px; below that the badges go above.
              (needsVape
                ? 'grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))]'
                : 'grid-cols-[repeat(auto-fit,minmax(min(100%,270px),1fr))]')
            }
          >
            {/* The entrance rides the two cards rather than the row —
                `contents` leaves no box to animate on a phone. In sync
                they read exactly as the row did. */}
            <div data-product-badges className="sss-rise relative flex flex-wrap content-center items-start justify-around gap-2 rounded-[18px] border border-hairline bg-surface px-4 py-5.5 [animation-delay:450ms] max-[559px]:order-6">
              {badges.map((badge) => (
                // Width follows the card so four badges stay on one
                // line in the half-panel column as well.
                <div
                  key={badge.label}
                  className={`flex flex-col items-center gap-2.5 ${hasTextDetails ? 'min-w-0 flex-1' : 'w-[clamp(64px,19%,76px)]'}`}
                >
                  <span className="flex size-[58px] items-center justify-center rounded-full border border-hairline text-ink transition-[border-color,transform] duration-200 hover:scale-[1.08] hover:border-[color:var(--sss-accent)]">
                    {badge.icon}
                  </span>
                  <span className="text-center text-[12px] font-medium leading-[1.3] text-ink-muted">
                    {badge.label}
                  </span>
                </div>
              ))}
            </div>

            <div
              className={
                'sss-rise relative flex flex-col justify-center gap-3 [animation-delay:450ms] max-[559px]:order-2' +
                (vapeOpen ? ' z-30' : '')
              }
            >
              {(needsVape || product.material) && (
                <div
                  className="relative flex items-stretch rounded-[18px] border border-hairline bg-surface transition-colors duration-200"
                >
                  {/* On a vape case the device picker takes the material
                      line's slot — the fit matters more than the resin. */}
                  {needsVape ? (
                    <VapePicker
                      vapes={product.vapes}
                      kind={choiceKind}
                      value={vapeId}
                      onChange={setVapeId}
                      onOpenChange={setVapeOpen}
                    />
                  ) : (
                    /* The row grows with the material line instead of
                       cutting it: the cell is only half the panel wide
                       and the line often needs two or three lines. */
                    <span className="flex min-w-0 flex-1 items-center px-5 py-3 text-[15px] font-medium leading-[1.35]">
                      {product.material}
                    </span>
                  )}
                </div>
              )}

              {!product.inStock ? (
                <Button variant="primary" disabled className="w-full max-[559px]:h-13">
                  {t('product.sold_out')}
                </Button>
              ) : (
                <>
                  {customization}
                  {/* The glinting accent CTA is the page's only money
                      action now — buy-now is gone, so every purchase
                       goes through the bag and its shipping checkout.
                      A vape case holds it back, greyed, until the
                      device is picked. */}
                  {vapeMissing ? (
                    <button
                      type="button"
                      disabled
                      className="flex min-h-[58px] w-full cursor-not-allowed items-center gap-4 rounded-[14px] bg-surface-sunk px-5.5 text-[16px] font-bold tracking-[-0.01em] text-ink-faint"
                    >
                      <span className="min-w-0 flex-1 truncate text-center">{t(`product.${choiceKind}.pick_first`)}</span>
                      <span className="shrink-0 whitespace-nowrap tabular-nums">
                        {formatPrice(product.price, product.prices)}
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={addToCart}
                      className="sss-glint flex min-h-[58px] w-full cursor-pointer items-center gap-4 rounded-[14px] bg-signal px-5.5 text-[16px] font-bold tracking-[-0.01em] text-on-accent transition-[transform,background-color] duration-200 ease-standard hover:scale-[1.015] hover:bg-signal-deep active:scale-[0.99]"
                    >
                      <span className="min-w-0 flex-1 truncate text-center">{t('product.add_to_bag')}</span>
                      <span className="shrink-0 whitespace-nowrap tabular-nums">
                        {formatPrice(product.price, product.prices)}
                      </span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Fits your Max — the charging clip and the bands guide ===== */}
      {product.fitGuide && <FitGuide guide={product.fitGuide} />}

      {/* ===== Previous customs — what left the workshop in this line ===== */}
      {product.custom && product.examples.length > 0 && <Examples examples={product.examples} />}

      {/* ===== Made by hand — case demo and material details ===== */}
      <section id="product-handmade" className="mt-20 overflow-hidden px-[clamp(16px,3vw,32px)] pt-2">
        <div className="flex flex-col items-center gap-4 text-center">
          <h2 className="sss-display -rotate-[1.5deg] text-[length:var(--sss-size-section)] font-normal">
            {t('product.made.title')}
          </h2>
          {kind !== 'stand' && <div className="mt-1.5 flex items-center gap-2.5">
            {hasDemo && (
              <TabButton active={activeTab === 'case'} onClick={() => setTab('case')}>
                {t('product.made.case')}
              </TabButton>
            )}
            <TabButton
              active={activeTab === 'materials'}
              onClick={() => setTab('materials')}
              className="-rotate-2"
            >
              {t('product.made.materials')}
            </TabButton>
          </div>}
        </div>

        <div className="relative mt-7 flex items-center justify-center py-10">
          {product.cutoutUrl && <CutoutMarquee src={product.cutoutUrl} />}
          <div className="relative min-w-[min(100%,256px)] flex-[0_1_496px]">
            {/* The tilted echo card behind the real one. */}
            {!showDetailsCopy && <div
              aria-hidden="true"
              className="absolute -bottom-5 -right-7 top-6 w-[45%] rotate-6 rounded-[20px] border border-hairline bg-surface"
            />}
            <div className={showDetailsCopy
              ? 'relative rounded-[18px] border border-hairline bg-surface px-6 py-5.5'
              : 'relative overflow-hidden rounded-[24px] border border-hairline bg-surface p-[clamp(28px,4vw,48px)]'}>
              {!showDetailsCopy && <GravityStars />}
              {/* Demos keep their presentation height; prose gets a compact text card. */}
              <div className={showDetailsCopy ? 'relative text-left' : `relative flex flex-col items-center justify-center gap-5.5 text-center ${
                hasDemo ? (isCompact ? 'min-h-[520px]' : 'min-h-[620px]') : 'min-h-[320px]'
              }`}>
                {kind !== 'stand' && activeTab === 'case' && hasDemo ? (
                  <ProductDemo videoUrl={product.demoVideoUrl} cutoutUrl={product.cutoutUrl} category={product.category} />
                ) : (
                  <>
                    {!hasTextDetails && <dl className="m-0 w-full max-w-[560px] overflow-hidden rounded-[14px] border border-hairline bg-background text-left">
                      {specs.map((spec, i) => (
                        <div
                          key={spec.label}
                          className={`flex items-baseline justify-between gap-6 px-4.5 py-3 ${
                            i > 0 ? 'border-t border-hairline' : ''
                          }`}
                        >
                          <dt className="whitespace-nowrap text-[14px] text-ink-faint">
                            {spec.label}
                          </dt>
                          <dd className="m-0 text-right text-[14px] font-medium text-ink">
                            {spec.value}
                          </dd>
                        </div>
                      ))}
                    </dl>}
                    {hasTextDetails && (
                      <p className="m-0 whitespace-pre-line text-[15px] leading-[1.6] text-ink-muted">
                        {t(kind === 'vape' ? 'product.made.vape_copy' : 'product.made.stand_copy')}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Choose your piece — the rest of the shop ===== */}
      <section className="mx-auto mt-16 max-w-[1440px] px-[clamp(16px,3vw,32px)]">
        {others.length > 0 && (
          <div className="sss-glass-band relative overflow-hidden rounded-[24px] border border-[color:var(--gb-panel-edge)] bg-[color:var(--gb-panel)] px-[clamp(18px,3vw,40px)] pb-[clamp(30px,3.5vw,48px)] pt-[clamp(28px,3vw,44px)]">
            <GlassBandGlow />

            <div className="relative mb-7 flex flex-wrap items-center justify-between gap-5">
              <h2 className="sss-display text-[clamp(38px,5vw,56px)] font-normal">
                {t('product.choose.title')}
              </h2>
              <Link
                href="/#shop"
                className="inline-flex items-center rounded-xl border border-[color:var(--gb-pill-edge)] bg-[color:var(--gb-pill)] px-6.5 py-4 text-[14px] font-bold text-ink backdrop-blur-[14px] transition-colors duration-200 hover:border-[color:var(--sss-ink)] hover:bg-ink hover:text-ink-inverse"
              >
                {t('product.choose.view_all')}
              </Link>
            </div>

            {/* Three up, as the sheet lays them out — two on a tablet,
                one on a phone, where a 240px render has the column to
                itself. A shop down to its last pieces takes as many
                columns as it has cards, rather than leaving the sheet's
                third slot standing empty. */}
            <div
              className={`relative grid gap-4 max-[899px]:grid-cols-2 max-[559px]:grid-cols-1 ${
                others.length >= 3
                  ? 'grid-cols-3'
                  : others.length === 2
                    ? 'grid-cols-2'
                    : 'grid-cols-1'
              }`}
            >
              {others.map((other) => (
                <CutoutCard key={other.id} product={other} />
              ))}
            </div>
          </div>
        )}

        <ClubBand />
      </section>

      <StampDialog
        open={open}
        onOpenChange={setOpen}
        text={stamp}
        style={style}
        onSave={(next, nextStyle) => {
          setStamp(next)
          setStyle(nextStyle)
          setOpen(false)
        }}
      />
    </Layout>
  )
}

/** A hairline card of the buy panel with the sheet's 18px title. */
function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative rounded-[18px] border border-hairline bg-surface px-6 py-5.5 transition-[border-color,transform] duration-200 ease-standard hover:-translate-y-0.5 hover:border-[color:var(--sss-ink-faint)]">
      <h2 className="mb-3.5 text-[18px] font-bold tracking-[-0.01em]">{title}</h2>
      {children}
    </div>
  )
}

/**
 * The stand's fit guide, straight under the hero: the clip of the
 * headphones dropping onto the dock, and beside it the one thing that
 * goes wrong — bands fully retracted miss the pins — as a do / don't
 * pair on white, each stamped with its verdict.
 */
function FitGuide({ guide }: { guide: NonNullable<Product['fitGuide']> }) {
  const { t } = useTranslation()

  const shots = [
    {
      src: guide.okUrl,
      alt: t('product.fit.ok_alt'),
      label: t('product.fit.ok_label'),
      ok: true,
    },
    {
      src: guide.noUrl,
      alt: t('product.fit.no_alt'),
      label: t('product.fit.no_label'),
      ok: false,
    },
  ]

  return (
    <section className="mx-auto mt-6 max-w-[1440px] px-[clamp(16px,3vw,32px)]">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] gap-4">
        {/* Same card as the bands guide beside it: title, the line
            under it, then the picture — here the clip, framed like the
            two photos. */}
        <div className="relative flex flex-col rounded-[24px] border border-hairline bg-surface p-[clamp(22px,3vw,32px)]">
          <h2 className="mb-1.5 text-[18px] font-bold tracking-[-0.01em]">
            {t('product.fit.video_copy')}
          </h2>
          <VisibleMedia className="mt-auto aspect-video w-full overflow-hidden rounded-[14px] border border-hairline bg-black">
          <SelectedVideo
            src={guide.videoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="block size-full object-cover"
          />
          </VisibleMedia>
        </div>

        <div className="relative rounded-[24px] border border-hairline bg-surface p-[clamp(22px,3vw,32px)]">
          <h2 className="mb-1.5 text-[18px] font-bold tracking-[-0.01em]">
            {t('product.fit.title')}
          </h2>
          <div className="grid grid-cols-2 gap-3.5">
            {shots.map((shot) => (
              <div key={shot.src} className="flex flex-col gap-2.5">
                <div className="relative overflow-hidden rounded-[14px] border border-hairline bg-white">
                  <StoreImage
                    src={shot.src}
                    alt={shot.alt}
                    loading="lazy"
                    className="block aspect-square w-full object-cover"
                  />
                  <span
                    className={`absolute left-3 top-3 flex size-[52px] items-center justify-center rounded-full text-white shadow-[0_4px_14px_rgba(0,0,0,0.35)] ${
                      shot.ok
                        ? 'bg-[color:var(--sss-success)]'
                        : 'bg-[color:var(--sss-destructive)]'
                    }`}
                  >
                    {shot.ok ? (
                      <CheckIcon size={30} strokeWidth={3} />
                    ) : (
                      <CloseIcon size={28} strokeWidth={3} />
                    )}
                  </span>
                </div>
                <span
                  className={`text-[14px] font-semibold leading-[1.35] ${
                    shot.ok
                      ? 'text-[color:var(--sss-success)]'
                      : 'text-[color:var(--sss-destructive)]'
                  }`}
                >
                  {shot.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

/** Bottom-frame the insertion demo so the case stays visible without a full-height portrait player. */
function ProductDemo({ videoUrl, cutoutUrl, category }: {
  videoUrl: string | null
  cutoutUrl: string | null
  category: string | null
}) {
  const { t } = useTranslation()
  const drop = productDropFor(videoUrl, cutoutUrl)
  const copy = category === 'vape_cases'
    ? t('product.demo.vape_copy')
    : category === 'lighter_cases'
      ? t('product.demo.lighter_copy')
      : t('product.demo.copy')

  return (
    <div id="product-demo" className="w-full">
      <h2 className="mb-1.5 text-[18px] font-bold tracking-[-0.01em]">
        {t(category === 'vape_cases' ? 'product.demo.vape_title' : 'product.demo.title')}
      </h2>
      {category !== 'vape_cases' && (
        <p className="m-0 mb-4.5 text-[15px] leading-[1.6] text-ink-muted">{copy}</p>
      )}
      <VisibleMedia className="relative mx-auto aspect-[5/6] w-full max-w-[405px] overflow-hidden rounded-[14px] border border-hairline bg-black">
        {drop ? (
          <ProductDrop scene={drop} alt={t('product.demo.title')} />
        ) : videoUrl ? (
          <SelectedVideo
            src={videoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={t('product.demo.title')}
            className="absolute bottom-0 left-0 block aspect-[9/16] w-full max-w-none"
          />
        ) : null}
      </VisibleMedia>
    </div>
  )
}

/**
 * On a made-to-order slot: the pieces of the same line that already
 * left the workshop, as 4:5 cards that open the piece. Proof of the
 * hand, not a menu — the copy says yours will be its own thing.
 */
function Examples({ examples }: { examples: Example[] }) {
  const { t } = useTranslation()

  return (
    <section className="mx-auto mt-20 max-w-[1440px] px-[clamp(16px,3vw,32px)]">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-3 border-b border-hairline pb-3.5">
        <h2 className="sss-display text-[clamp(36px,4.6vw,60px)] font-normal">
          {t('product.examples.title')}
        </h2>
        <span className="text-[14px] text-ink-faint">{t('product.examples.sub')}</span>
      </div>
      <div className="mt-5.5 grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-4 max-[559px]:grid-cols-2">
        {examples.map((example) => (
          <ExampleCard key={example.id} listed={example.listed} href={`/products/${example.id}`}>
            <div className="relative aspect-[4/5] overflow-hidden rounded-[18px] border border-hairline bg-surface">
              <StoreImage
                src={example.imageUrl}
                alt={example.name}
                loading="lazy"
                className="absolute inset-0 size-full object-cover transition-transform duration-500 ease-standard group-hover:scale-[1.04]"
              />
            </div>
            <div className="flex justify-between gap-2 px-0.5 pt-3">
              <span className="text-[15px] font-semibold leading-[1.25]">
                {splitName(example.name).title}
              </span>
              <span className="shrink-0 text-[14px] text-ink-faint">
                {t('product.examples.line')}
              </span>
            </div>
          </ExampleCard>
        ))}
      </div>
    </section>
  )
}

/** A card links to its piece while the piece is in the shop; past work
 *  that left the shop is shown, not linked. */
function ExampleCard({
  listed,
  href,
  children,
}: {
  listed: boolean
  href: string
  children: React.ReactNode
}) {
  const className = 'group min-w-0 text-ink hover:text-ink'
  return listed ? (
    <Link href={href} className={className}>
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  )
}

/**
 * The hero's media half. Photos open the piece. The pager is one
 * right-hand rail at every width: a piece that has also been scanned
 * carries the round "3D" button above the arrows on a wide window
 * (turn 2a of the V3 switcher sheet), while on a phone the rail keeps
 * only the left / right arrows — no 3D button. A single photo drops
 * the arrows.
 */
function HeroMedia({ product }: { product: Product }) {
  const { t } = useTranslation()
  const [slide, setSlide] = useState(0)
  const [playedDrops, setPlayedDrops] = useState<Set<number>>(() => new Set())
  const [is3d, setIs3d] = useState(false)
  /**
   * The GLB runs to a few megabytes, so it is fetched the first time
   * the 3D view is asked for — and then left mounted, so flipping back
   * to the photos and out again costs nothing and the camera stays
   * where the customer left it.
   */
  const [loaded, setLoaded] = useState(false)
  // A reveal clip rides the slider as its last slide.
  const drop = productDropFor(product.demoVideoUrl, product.cutoutUrl)
  const photoOffset = drop ? 1 : 0
  const count = photoOffset + product.images.length + (product.videoUrl ? 1 : 0)
  // Aliased so the checker carries `has3d` down to the viewer below
  // and knows the path is there.
  const modelUrl = product.modelUrl
  const has3d = modelUrl !== null

  // An inset card, not a full-bleed half: the page colour runs around
  // it as a gutter, the way the sheet frames the shot. Beside the buy
  // panel the card fills the screen height less that gutter — a pixel
  // cap made it stop short of the fold on a large monitor.
  // The gutter is `--sss-frame`, the same number the header pads by,
  // so the wordmark chip keeps one constant inset inside this corner.
  // The photo layer paints the sunk surface; the 3D layer does not —
  // see below.
  const frame =
    'sss-media-in relative m-[var(--sss-frame)] min-h-[calc(100svh-var(--sss-frame)*2)] overflow-hidden rounded-[26px] max-[919px]:min-h-[min(60vh,560px)]'

  if (count === 0 && !has3d) {
    return (
      <div className={`${frame} flex items-center justify-center bg-surface-sunk text-ink-faint`}>
        <ImagePlaceholderIcon size={64} />
      </div>
    )
  }

  const step = (dir: 1 | -1) => setSlide((s) => (s + dir + count) % count)
  const toggle3d = () => {
    setLoaded(true)
    setIs3d((on) => !on)
  }

  const arrows = (
    <>
      <PagerButton label={t('product.prev_image')} onClick={() => step(-1)} nudge="hover:-translate-x-[3px]">
        <ArrowLeftIcon size={20} />
      </PagerButton>
      <PagerButton label={t('product.next_image')} onClick={() => step(1)} nudge="hover:translate-x-[3px]">
        <ArrowRightIcon size={20} />
      </PagerButton>
    </>
  )

  return (
    <div className={frame} aria-label={t('product.pager_aria')}>
      {/* The photos sink back a hair as the scene takes over, so the
          flip reads as the piece coming forward rather than two
          pictures swapping places. */}
      <div
        className="absolute inset-0 bg-surface-sunk transition-[opacity,transform] duration-[320ms] ease-standard"
        style={{
          opacity: is3d ? 0 : 1,
          transform: `scale(${is3d ? 0.97 : 1})`,
          pointerEvents: is3d ? 'none' : 'auto',
        }}
      >
        {!is3d && drop && slide === 0 && (
          <div
            aria-hidden={slide !== 0}
            className="absolute inset-0 transition-opacity duration-500 ease-standard"
            style={{ opacity: slide === 0 ? 1 : 0 }}
          >
            <ProductDrop key={product.id} scene={drop} alt={product.name} static={playedDrops.has(product.id)} onSettled={() => setPlayedDrops(previous => new Set(previous).add(product.id))} />
          </div>
        )}
        {!is3d && product.images.map((image, index) => index + photoOffset === slide && (
          <StoreImage
            key={image.id}
            src={image.url}
            fetchPriority={slide === 0 ? 'high' : 'auto'}
            loading="eager"
            alt={product.name}
            aria-hidden={index + photoOffset !== slide}
            className="absolute inset-0 size-full object-cover transition-opacity duration-500 ease-standard"
            style={{ opacity: index + photoOffset === slide ? 1 : 0 }}
          />
        ))}
        {!is3d && product.videoUrl && slide === count - 1 && (
          <SelectedVideo
            src={product.videoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 size-full object-cover"
          />
        )}
      </div>

      {/* No card under the scene: the piece floats on the page over the
          same star field the buy panel has, so the hero reads as one
          surface rather than a grey box beside a panel. Hence the sunk
          surface living on the photo layer and not on the frame. */}
      {has3d && (
        <div
          aria-hidden={!is3d}
          className="absolute inset-0 transition-[opacity,transform] duration-[320ms] ease-standard"
          style={{
            opacity: is3d ? 1 : 0,
            transform: `scale(${is3d ? 1 : 1.04})`,
            pointerEvents: is3d ? 'auto' : 'none',
          }}
        >
          <GravityStars />
          {/* The viewer sizes itself 100%/100%, which needs a definite
              box — the frame only has a min-height, so pin it. */}
          {loaded && (
            <div className="absolute inset-0">
              <ModelViewer
                src={modelUrl}
                alt={`${product.name} — interactive 3D view`}
                // The framed radius, not a percentage of it: the hero
                // card is tall enough to take the whole bounding sphere,
                // and framing it is the one distance that cannot crop
                // the piece at any angle the idle turn passes through.
                // Pulling in to 78% — which suits a shorter card — cut
                // the lighter off above and below.
                cameraOrbit="25deg 62deg auto"
              />
            </div>
          )}
        </div>
      )}

      {(has3d || count > 1) && (
        /* One right-hand rail at every width. On a phone it carries the
           3D round above the left / right pair, and on a wide window the
           same controls stay together. */
        <div className="absolute right-5 top-1/2 z-[4] flex -translate-y-1/2 flex-col gap-2.5">
          {has3d && (
            <button
              type="button"
              aria-label={t('product.view_3d')}
              aria-pressed={is3d}
              onClick={toggle3d}
              className={`flex size-[52px] cursor-pointer items-center justify-center rounded-full border bg-surface text-[13px] font-bold transition-[border-color,color] duration-200 ease-standard ${
                is3d
                  ? 'border-signal text-signal'
                  : 'border-hairline text-ink hover:border-[color:var(--sss-ink)]'
              }`}
            >
              3D
            </button>
          )}
          {count > 1 && (
            // In the 3D view the arrows have nothing left to step, so
            // they step out and leave the card to the piece.
            <div
              aria-hidden={is3d}
              className="flex flex-col gap-2.5 transition-opacity duration-300 ease-standard"
              style={{ opacity: is3d ? 0 : 1, pointerEvents: is3d ? 'none' : 'auto' }}
            >
              {arrows}
            </div>
          )}
        </div>
      )}

      {/* The chip counts the photos, and hands the model its one
          instruction once there are no photos to count. */}
      {(count > 1 || is3d) && (
        <span className="absolute bottom-5 left-5 z-[4] rounded-full border border-hairline bg-surface/80 px-3.5 py-2 text-[13px] font-medium tabular-nums text-ink backdrop-blur-[10px]">
          {is3d
            ? t('product.drag_to_rotate')
            : `${String(slide + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}`}
        </span>
      )}
    </div>
  )
}

function PagerButton({
  label,
  onClick,
  nudge,
  children,
}: {
  label: string
  onClick: () => void
  /** The full hover-translate utility, written literally at the call
   *  site so Tailwind's scanner actually sees it. */
  nudge: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`flex size-[52px] cursor-pointer items-center justify-center rounded-full border border-hairline bg-surface text-ink transition-[background-color,border-color,color,transform] duration-200 ease-standard hover:border-[color:var(--sss-ink)] hover:bg-ink hover:text-ink-inverse ${nudge}`}
    >
      {children}
    </button>
  )
}

/** Circle-bordered tab from the sheet — accent ring when active. */
function TabButton({
  active,
  onClick,
  className,
  children,
}: {
  active: boolean
  onClick: () => void
  className?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`cursor-pointer rounded-full border-2 bg-transparent px-6 py-3 text-[15px] font-bold tracking-[0.01em] transition-colors duration-200 hover:text-signal ${
        active ? 'border-[color:var(--sss-accent)] text-ink' : 'border-transparent text-ink'
      } ${className ?? ''}`}
    >
      {children}
    </button>
  )
}

/**
 * The made-by-hand band's drifting strip of cutout renders. Two
 * identical halves with a uniform gap, so the -50% marquee loops
 * without a seam; heights and tilts cycle per the V3 sheet.
 *
 * A half has to be at least as wide as the viewport, or the strip runs
 * out before the loop point and the band visibly empties and restarts.
 * The narrowest cutout is a lighter about 100px at these heights, so
 * 24 per half clears ~4300px — past a maximised 4K window, and well
 * past every width the `min-[920px]` gate lets through.
 *
 * Desktop only. On a phone the strip is wider than the viewport and
 * the spec card covers most of it, so it reads as clutter behind the
 * text rather than as a band.
 */
function CutoutMarquee({ src }: { src: string }) {
  const pattern = [
    'h-[300px]',
    'h-[250px] -rotate-[6deg]',
    'h-[290px] rotate-[4deg]',
    'h-[260px] -rotate-[3deg]',
  ]
  const half = Array.from({ length: 24 }, (_, i) => pattern[i % pattern.length])

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-screen -translate-x-1/2 items-center overflow-hidden min-[920px]:flex"
    >
      <div className="sss-marquee flex w-max shrink-0 items-center gap-[clamp(26px,3.2vw,58px)] pr-[clamp(26px,3.2vw,58px)]">
        {[...half, ...half].map((cls, i) => (
          <StoreImage key={i} src={src} alt="" loading="lazy" className={`w-auto ${cls}`} />
        ))}
      </div>
    </div>
  )
}

/** Ambient CSS lamps preserve the glass treatment without separate image requests. */
function GlassBandGlow() {
  const spots: {
    inset: React.CSSProperties
    width: number
    blur: number
    /** The sheet lights the first lamp a step brighter than the pair. */
    lead?: boolean
  }[] = [
    { inset: { left: '4%', top: '18%' }, width: 330, blur: 64, lead: true },
    { inset: { left: '42%', bottom: '-12%' }, width: 360, blur: 70 },
    { inset: { right: '2%', top: '8%' }, width: 320, blur: 64 },
  ]

  return (
    <>
      {spots.map((spot, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="pointer-events-none absolute aspect-square max-w-none bg-[radial-gradient(ellipse,var(--sss-accent)_0%,transparent_70%)]"
          style={{
            ...spot.inset,
            width: spot.width,
            filter: `blur(${spot.blur}px) saturate(var(--gb-glow-sat))`,
            opacity: `var(${spot.lead ? '--gb-glow-lead' : '--gb-glow'})`,
          }}
        />
      ))}
    </>
  )
}

/**
 * Option 1a from the glass sheet — a frosted card rather than a cutout
 * block. It has no fill of its own: heavy backdrop blur over the panel's
 * ambient light, a light frost wash, a hairline edge and one inset
 * highlight along the top. Colour comes from whatever is underneath, so
 * both themes get the same card and the values flip in CSS.
 *
 * The render floats over it, and under both stands the sheet's own
 * line: what the piece is, and its price opposite. No hover needed —
 * the card says what it costs before it is touched.
 */
function CutoutCard({ product }: { product: OtherProduct }) {
  return (
    <div className="sss-glass-card group relative flex flex-col gap-3.5 rounded-[18px] px-4.5 pb-4.5 pt-6">
      <Link
        href={`/products/${product.id}`}
        className="sss-graffiti relative px-1 text-center text-[26px] leading-[0.95] text-ink hover:text-ink"
      >
        {splitName(product.name).title}
      </Link>
      <Link
        href={`/products/${product.id}`}
        aria-label={product.name}
        className="relative flex h-[240px] items-center justify-center"
      >
        <StoreImage
          src={product.cutoutUrl}
          alt={product.name}
          loading="lazy"
          className="max-h-full max-w-[82%] object-contain [filter:var(--gb-cast)] transition-transform duration-[350ms] ease-standard group-hover:-rotate-2 group-hover:scale-105"
        />
      </Link>
      <CardMeta
        category={product.category}
        material={product.material}
        price={product.price}
        prices={product.prices}
      />
    </div>
  )
}

/**
 * Compact shop links above the shared footer's brand card.
 */
function ClubBand() {
  const { t } = useTranslation()

  const links = [
    { href: '/#shop', label: t('product.club.shop') },
    { href: '/returns', label: t('product.club.faq') },
    { href: 'mailto:support@sickstuff.shop', label: t('product.club.contact') },
  ]
  const linkClass =
    'border-t border-hairline px-1 py-4 text-[15px] font-bold tracking-[0.01em] text-ink transition-[color,padding-left] duration-200 ease-standard hover:pl-3 hover:text-signal'

  return (
    <div className="mt-8 pt-2">
      <nav aria-label={t('product.club.shop')} className="flex flex-col">
        {links.map((link) =>
          link.href.startsWith('mailto:') ? (
            <a key={link.label} href={link.href} className={`${linkClass} border-b`}>
              {link.label}
            </a>
          ) : (
            <Link key={link.label} href={link.href} className={linkClass}>
              {link.label}
            </Link>
          )
        )}
      </nav>
    </div>
  )
}

/** Square 48 control beside the committed line — pencil, then bin. */
function IconButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  const props = {
    variant: 'ink' as const,
    'aria-label': label,
    onClick,
    className: ICON_BUTTON_CLASS,
  }

  return (
    <Suspense fallback={<Button {...props}>{children}</Button>}>
      <RippleButton {...props}>{children}</RippleButton>
    </Suspense>
  )
}
